/**
 * @fileoverview All settings — the Control Panel's home.
 *
 * A searchable index of every settings page, grouped, showing lock state. It
 * exists because a navigation menu answers "where do I click" only once you
 * know the name of the thing; this answers "what can be configured at all",
 * which is the question someone new to the system actually has.
 *
 * THE PROJECTION IS THE RECOVERED ONE, verbatim from the architecture note:
 * "All Settings derives locked from isVisible and filters the hydrated state by
 * isDiscoverable, context, display group, and the Show locked settings toggle."
 * Every clause of that sentence is a line of code below, and the order matters —
 * filter by discoverability FIRST, so a page this deployment does not have never
 * reaches the lock logic and cannot be revealed by the toggle.
 *
 * Locked rows are shown rather than omitted, following Foundry. Hiding them
 * teaches a reader the capability does not exist; showing it greyed teaches
 * them it exists and belongs to someone else, which is true and is what they
 * need in order to go and ask for it.
 *
 * STRUCTURE IS FROM THE CAPTURE (foundry-ui verbatim/control-panel-settings) and
 * the class names are Foundry's own, vendored — wearing the names is what makes
 * the layout appear, so the card, the grid columns and the row rhythm are
 * measured by them and not re-guessed by me.
 */

import { useMemo, useState } from "react";
import { Card, Classes, InputGroup, NonIdealState, Switch, Tag, Tooltip } from "@blueprintjs/core";
import { Link } from "react-router";

import {
  EXTENSION_DISPLAY_GROUPS,
  EXTENSION_DISPLAY_GROUP_LABELS,
  type ControlPanelExtensionDefinition,
  type ExtensionDisplayGroup,
  type HydratedExtension,
} from "../registry/ExtensionDefinition";
import {
  allSettingsExtension as ase,
  controlPanelInternalComponents as cpi,
} from "../../../../components/control-panel/foundry-parts.generated";
import { CmdKHint } from "../../../../components/command-palette";

export interface AllSettingsProps {
  readonly extensions: readonly HydratedExtension[];
  /** Scope-aware canonical URL supplied by the host's route adapter. */
  readonly hrefForExtension: (definition: ControlPanelExtensionDefinition) => string;
}

export function AllSettings({ extensions, hrefForExtension }: AllSettingsProps): React.ReactElement {
  const [query, setQuery] = useState("");
  const [showLocked, setShowLocked] = useState(true);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const visible = extensions
      .filter((e) => e.isDiscoverable)
      .filter((e) => showLocked || e.isVisible)
      .filter((e) => {
        if (!q) return true;
        const d = e.definition;
        /*
          Keywords are searched alongside name and description, which is the
          entire reason the field exists in the real contract: someone hunting
          for "MFA" must land on Authentication without knowing we called it
          that.
        */
        return (
          d.displayName.toLowerCase().includes(q) ||
          d.description.toLowerCase().includes(q) ||
          d.keywords.some((k) => k.toLowerCase().includes(q))
        );
      });

    return EXTENSION_DISPLAY_GROUPS.map(
      (g) => [g, visible.filter((e) => e.definition.displayGroup === g)] as const,
    ).filter(([, items]) => items.length > 0);
  }, [extensions, query, showLocked]);

  const lockedCount = extensions.filter((e) => e.isDiscoverable && !e.isVisible).length;

  return (
    <>
      <div className={ase["wrapper"]}>
        <div className={ase["header-wrapper"]}>
          <div className={ase["header"]}>
            <div className={ase["header-content"]}>
              <h2 className={`${Classes.HEADING} ${ase["no-margin"]}`}>All settings</h2>
            </div>
            <InputGroup
              className={cpi["search-bar"]}
              leftIcon="search"
              placeholder="Search settings…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="all-settings-search"
            />
            {/*
              The keyboard hint sits BESIDE the field, not inside it — Foundry
              gives it its own element (`__search-bar-shortcut-text`) so it does
              not scroll with the typed value or overlap a long query.
            */}
            <div className={cpi["search-bar-shortcut-text"]}>
              <CmdKHint />
            </div>
          </div>
        </div>
      </div>

      <div className={ase["content"]}>
        <div className={ase["extension-container"]}>
          {/*
            Only offered when there is something to reveal. A toggle that
            changes nothing is a control that teaches the reader their input
            does not matter.
          */}
          {lockedCount > 0 && (
            <Switch
              checked={showLocked}
              onChange={(e) => setShowLocked(e.currentTarget.checked)}
              label={`Show locked settings (${lockedCount})`}
              data-testid="all-settings-show-locked"
            />
          )}

          {grouped.length === 0 ? (
            <NonIdealState icon="search" title="No settings match" description="Try a different term." />
          ) : (
            grouped.map(([group, items]) => (
              <SettingsGroupCard
                key={group}
                group={group}
                items={items}
                hrefForExtension={hrefForExtension}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}

function SettingsGroupCard({
  group,
  items,
  hrefForExtension,
}: {
  group: ExtensionDisplayGroup;
  items: readonly HydratedExtension[];
  hrefForExtension: (definition: ControlPanelExtensionDefinition) => string;
}): React.ReactElement {
  return (
    <div className={ase["extension-group"]}>
      <div className={cpi["content-card@9tndr6"]}>
        <div className={cpi["title-container@9tndr6"]}>
          <h5 className={`${Classes.HEADING} ${cpi["title-text"]}`}>
            {EXTENSION_DISPLAY_GROUP_LABELS[group]}
          </h5>
        </div>
        <Card elevation={0}>
          {items.map(({ definition, isVisible, reason }) => {
            const locked = !isVisible;
            return (
              <Link
                key={definition.id}
                to={locked ? "" : hrefForExtension(definition)}
                data-testid={`all-settings-item-${definition.id}`}
                aria-disabled={locked || undefined}
                onClick={(e) => {
                  if (locked) e.preventDefault();
                }}
                className={[
                  ase["two-col-grid"],
                  locked ? "" : ase["select-on-hover"],
                  "bp-all-settings-row",
                  locked ? "bp-all-settings-row-locked" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className={ase["information"]}>
                  <h6 className={`${Classes.HEADING} ${ase["no-margin"]}`}>{definition.displayName}</h6>
                  <div className="bp6-text-disabled">{definition.description}</div>
                </div>
                <div className={ase["icons"]}>
                  {/*
                    A locked row says WHY, as Foundry's does — its own lock
                    affordance is a tag in a tooltip
                    (`control-panel-shell__lock-tooltip`). The tooltip carries
                    the hydration reason, so "missing required operations:
                    control-panel:enrollment:view-users" is what an
                    administrator can take to whoever grants it.
                  */}
                  {locked && (
                    <Tooltip content={reason ?? "Not available"} minimal>
                      <Tag minimal icon="lock">
                        Locked
                      </Tag>
                    </Tooltip>
                  )}
                </div>
              </Link>
            );
          })}
        </Card>
      </div>
    </div>
  );
}
